import { useState, useEffect, useMemo, useRef } from "react";
import { nav_links } from "./NavItem";
import { ChevronDown } from "lucide-react";
import Icon, { icons } from "./icon/Icon";
import { Link, useLocation } from "react-router-dom";
import SubMenu from "./sub-menu";
import { usePreviewSession } from "../../../../../contexts/PreviewSessionContext";
import { resolvePageKey, PERSPECTIVE_TABS } from "../../../../../lib/preview-pages";
import type { NavLink, Category, SubLink } from "../../../../../types/drawer/NavItem";

interface NavigationProps {
  isOpen: boolean;
}

function pathAllowed(href: string | undefined, allowed: Set<string>): boolean {
  if (!href) return false;
  if (href === "/perspectives") {
    return PERSPECTIVE_TABS.some((t) => allowed.has(`perspectives.${t.id}`));
  }
  const [pathname, query] = href.split("?");
  const key = resolvePageKey(pathname, query);
  if (!key) return false;
  return allowed.has(key);
}

function filterNav(links: NavLink[], allowed: Set<string>): NavLink[] {
  const result: NavLink[] = [];
  for (const link of links) {
    if (link.children) {
      const filteredCats: Category[] = link.children
        .map((cat) => ({
          ...cat,
          links: cat.links.filter((sub: SubLink) => pathAllowed(sub.href, allowed)),
        }))
        .filter((cat) => cat.links.length > 0);
      if (filteredCats.length === 0) continue;
      const newHref = filteredCats[0].links[0]?.href ?? link.href;
      result.push({ ...link, href: newHref, children: filteredCats });
    } else if (pathAllowed(link.href, allowed)) {
      result.push(link);
    }
  }
  return result;
}

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  show: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({ text, children, show }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHovered && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8,
      });
    }
  }, [isHovered]);

  return (
    <div 
      ref={ref}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {show && isHovered && (
        <div 
          className="fixed z-[9999] pointer-events-none"
          style={{ 
            top: position.top,
            left: position.left,
            transform: 'translateY(-50%)'
          }}
        >
          <div className="bg-[#1a2744] text-[#DDEBFF] text-[13px] font-[300] px-3 py-1.5 rounded-md whitespace-nowrap shadow-lg border border-[#2a3a5c]">
            {text}
          </div>
        </div>
      )}
    </div>
  );
};

function Navigation(props: NavigationProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const previewCtx = usePreviewSession();

  const links = useMemo(() => {
    if (!previewCtx?.session) return nav_links;
    return filterNav(nav_links, new Set(previewCtx.session.allowedPages));
  }, [previewCtx?.session]);

  useEffect(() => {
    const activeParent = links.find((link) =>
      link.children?.some((category) =>
        category.links.some((sublink) => location.pathname === sublink.href)
      )
    );
    setOpenDropdown(activeParent?.text || null);
  }, [location.pathname, links]);

  const handleToggle = (name: string) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  return (
    <nav className="flex-grow mt-4">
      {links.map((link, index) => {
        const isMainLinkActive = location.pathname === link.href;
        const isSubLinkActive = link.children?.some((category) =>
          category.links.some((sublink) => location.pathname === sublink.href)
        );
        const isDropdownOpen = openDropdown === link.text;

        return link.children ? (
          <div key={index} className="relative">
            <Tooltip text={link.text} show={!props.isOpen}>
              <Link
                to={link.href || "#"}
                className={`flex group items-center ${
                  props.isOpen ? "justify-between" : "justify-center"
                } w-full h-[36px] px-[10px] leading-[20px] font-[300] text-[16px] transition-all duration-300 ${
                  isMainLinkActive || isSubLinkActive
                    ? "bg-[#0193F8] text-[#05050F] !font-[400]"
                    : "text-[#DDEBFF] hover:bg-[#0193F8] hover:text-[#05050F]"
                } ${
                  props.isOpen
                    ? "my-[0.8rem] rounded-[13px]"
                    : "justify-center !h-[50px] my-[0.5rem] rounded-[10px]"
                }`}
              >
                <div className="flex items-center">
                  <Icon
                    isOpen={props.isOpen}
                    isActive={!!(isMainLinkActive || isSubLinkActive)}
                    name={link.icon as keyof typeof icons}
                  />
                  {props.isOpen && <span>{link.text}</span>}
                </div>

                {props.isOpen && (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleToggle(link.text);
                    }}
                    className="cursor-pointer p-1 rounded-md hover:bg-white/10 transition"
                  >
                    <ChevronDown size={18} />
                  </div>
                )}
              </Link>
            </Tooltip>

            <SubMenu
              isOpen={props.isOpen}
              link={link}
              isDropdownOpen={!!isDropdownOpen}
            />
          </div>
        ) : (
          <Tooltip key={index} text={link.text} show={!props.isOpen}>
            <Link
              to={link.href || "/"}
              className={`flex items-center text-[#DDEBFF] group h-[36px] px-[10px] text-[16px] leading-[20px] font-[300] transition-all duration-300 ${
                isMainLinkActive
                  ? "bg-[#0193F8] !text-[#05050F] !font-[400]"
                  : "hover:bg-[#0193F8] hover:text-[#05050F]"
              } ${
                props.isOpen
                  ? "my-[0.9rem] rounded-[12px]"
                  : "justify-center !h-[50px] my-[0.5rem] rounded-[10px]"
              }`}
            >
              <Icon
                isOpen={props.isOpen}
                isActive={isMainLinkActive}
                name={link.icon as keyof typeof icons}
              />
              {props.isOpen && <span className="ml-0">{link.text}</span>}
            </Link>
          </Tooltip>
        );
      })}
    </nav>
  );
}

export default Navigation;
